import {
  getClickCancelOOPModalEvent,
  getClickContinueOOPModalEvent,
  getShowOOPModalEvent,
  isCorpTenant,
  SHOW_POLICIES_IN_PACKAGES,
  useShowPolicyBanner,
} from "@capone/common";
import { Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  HotelShopRoomTypePickerEnum,
  HotelShopRoomTypePickerVariant,
  Icon,
  IconName,
  PackageHotelShopRoomTypePicker,
  PricingDisplayModeEnum,
  TrackingEventControlType,
  SanitizedHtml,
} from "halifax";
import React, { useContext, useEffect, useRef, useState } from "react";
import { RouteComponentProps } from "react-router";
import {
  ModalScreens,
  POLICY_DESCRIPTOR,
  PolicyViolation,
  RoomInfoProductsWithTransformedIndexes,
  SELECT_PACKAGE_HOTEL_ROOM,
  VIEWED_POLICY_DESCRIPTOR,
  ViewedCorpRateDescriptorEntryPoints,
} from "redmond";
import { AVAILABLE, useExperimentIsVariant } from "@capone/experiments";
import {
  useExperiments,
  getExperimentVariant,
  PACKAGES_LIST_OPTIMIZATIONS_V1,
  getExperimentVariantCustomVariants,
  PACKAGES_PRICING_DISPLAY,
  PACKAGES_PRICING_DISPLAY_VARIANTS,
  CHANGE_PER_TRAVELER,
} from "../../../../context/experiments";
import { ClientContext } from "../../../../App";
import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";

import { DesktopRoomPickerConnectorProps } from "./container";
import { onOpenCompareBarTooltip } from "../../../../utils/events";
import "./styles.scss";
import { getEarnTagText } from "../../textConstants";

export interface IDesktopRoomPickerProps
  extends DesktopRoomPickerConnectorProps,
    RouteComponentProps {
  roomInfoProductsType?: HotelShopRoomTypePickerVariant;
  handleReadyToRedirect?: (
    trackingEventControl?: TrackingEventControlType
  ) => void;
  setRoomInfoProduct?: (
    roomInfoProduct: RoomInfoProductsWithTransformedIndexes
  ) => void;
  setRoomProductIndex?: (roomProductIndex: number) => void;
  nonRefundablePolicyOverrideText?: string;
}

export const DesktopRoomPicker = (props: IDesktopRoomPickerProps) => {
  const {
    checkinDate,
    checkoutDate,
    history,
    adultsCount,
    children,
    handleReadyToRedirect,
    roomInfoProductsType,
    setRoomInfoProduct,
    setRoomProductIndex,
    canEarnRewards,
    fetchPackageHotelShop,
    selectedAccount,
    selectedRewardsAccount,
  } = props;
  const isFirstUpdate = useRef<boolean>(true);

  const [corpPolicyDescriptorViewCount, setCorpPolicyDescriptorViewCount] =
    useState(0);

  const { isAutoApprovalEnabled, policies } = useContext(ClientContext);

  const modalType = isAutoApprovalEnabled
    ? "out_of_policy_auto"
    : "out_of_policy";

  const showEarnEnhancement =
    !!selectedRewardsAccount &&
    !!selectedRewardsAccount.earn.hotelsMultiplier &&
    canEarnRewards;

  const { experiments } = useExperiments();
  const packagesPricingDisplayVariant = getExperimentVariantCustomVariants(
    experiments,
    PACKAGES_PRICING_DISPLAY,
    PACKAGES_PRICING_DISPLAY_VARIANTS
  );
  const isCorpHotelRoomRatesDesignTestEnabled = useExperimentIsVariant(
    "corp-hotel-room-rates-design-test-v1",
    AVAILABLE
  );
  const packagesListEnhancementsEnabled =
    getExperimentVariant(experiments, PACKAGES_LIST_OPTIMIZATIONS_V1) ===
    AVAILABLE;

  useEffect(() => {
    if (checkinDate && checkoutDate && (adultsCount || children)) {
      // skip the first update
      if (isFirstUpdate.current) {
        isFirstUpdate.current = false;
      } else {
        fetchPackageHotelShop(history, {
          forceCallPackageHotelAvailability: true,
        });
      }
    }
  }, [checkinDate, checkoutDate, adultsCount, children]);

  const showPolicyBanner =
    SHOW_POLICIES_IN_PACKAGES && useShowPolicyBanner(policies);

  const onOpenPolicyDescriptor = (
    entryPoint: string,
    reasons: PolicyViolation[]
  ) => {
    if (corpPolicyDescriptorViewCount <= 5) {
      trackEvent({
        eventName: VIEWED_POLICY_DESCRIPTOR,
        properties: {
          type: POLICY_DESCRIPTOR,
          entry_point: entryPoint,
          funnel: "packages",
          policy_reason: reasons.join(", "),
        },
      });
      setCorpPolicyDescriptorViewCount((prevState) => prevState + 1);
    }
  };

  return (
    <PackageHotelShopRoomTypePicker
      {...props}
      roomInfoProductsType={
        roomInfoProductsType ?? {
          roomInfoProducts: props.roomInfoProducts,
          variant: HotelShopRoomTypePickerEnum.Default,
        }
      }
      onClickContinue={(
        room_type?: string,
        in_policy?: boolean,
        hotel_loyalty_eligible?: boolean,
        is_corporate?: boolean
      ) => {
        if (handleReadyToRedirect) {
          handleReadyToRedirect({
            [SELECT_PACKAGE_HOTEL_ROOM]: {
              properties: {
                room_type,
                provider_name: props.chosenProduct?.providerName,
                rate_provider: props.chosenProduct?.providerName,
                ...(isCorpTenant(config.TENANT) && {
                  in_policy,
                  hotel_loyalty_eligible,
                  is_corporate,
                }),
              },
            },
          });
        }
      }}
      className={clsx("b2b", "desktop-room-picker")}
      reserveButtonClassName="reserve-cta"
      desktopReserveButtonText={<span>{`Continue for `}</span>}
      earnTagContent={
        showEarnEnhancement ? (
          <>
            <Icon name={IconName.StarIcon} />
            <SanitizedHtml
              as={Typography}
              className="earn-tag-text"
              html={getEarnTagText(
                selectedRewardsAccount.earn.hotelsMultiplier,
                selectedRewardsAccount.rewardsBalance.currencyDescription ??
                  selectedRewardsAccount.rewardsBalance.currency
              )}
            />
          </>
        ) : undefined
      }
      earnTagClassName={showEarnEnhancement ? "b2b" : undefined}
      setRoomInfoProduct={setRoomInfoProduct}
      setRoomProductIndex={setRoomProductIndex}
      policyLimit={policies?.hotels.policies[0].maxPricePerNight}
      onShowOutOfPolicyModal={() =>
        trackEvent(
          getShowOOPModalEvent(ModalScreens.HOTELS_SHOP, "packages", modalType)
        )
      }
      onClickOutOfPolicyContinue={() =>
        trackEvent(
          getClickContinueOOPModalEvent(
            ModalScreens.HOTELS_SHOP,
            "packages",
            modalType
          )
        )
      }
      onClickOutOfPolicyCancel={() =>
        trackEvent(
          getClickCancelOOPModalEvent(
            ModalScreens.HOTELS_SHOP,
            "packages",
            modalType
          )
        )
      }
      onOpenPolicyDescriptor={onOpenPolicyDescriptor}
      onViewCorpCompareBar={onOpenCompareBarTooltip(
        ViewedCorpRateDescriptorEntryPoints.HOTELS_ROOM_DETAILS
      )}
      showPolicyBanner={showPolicyBanner}
      rewardsAccount={selectedAccount}
      isCorpHotelRoomRatesDesignTestEnabled={
        isCorpHotelRoomRatesDesignTestEnabled
      }
      showEnhancedPackagePricing={packagesListEnhancementsEnabled}
      pricingDisplayMode={
        packagesPricingDisplayVariant === CHANGE_PER_TRAVELER
          ? PricingDisplayModeEnum.ChangePerTraveler
          : PricingDisplayModeEnum.TotalPerTraveler
      }
    />
  );
};
